import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';

import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

import { MOTION, ellipsis } from './chatTheme';

/**
 * Контекстное меню строки чата: правая кнопка на ПК, долгое нажатие на телефоне.
 *
 * Рисуется порталом в body и разворачивается к центру экрана у краёв —
 * список чатов длинный, и меню у нижней строки иначе уезжало бы за окно.
 *
 * «Покинуть чат» не показываем для чатов событий: сервер их покидать не даёт
 * (переписка по событию нужна для разбора, да и автоматика вернёт участника
 * обратно), и пункт, который всегда отвечает отказом, только раздражает.
 */

const MENU_W = 244;

export default function ChatContextMenu({
  anchor,          // { x, y }
  chat,
  onTogglePin,
  onAddToFolder,
  onMarkRead,
  onLeave,
  onClose,
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: anchor.x, top: anchor.y, origin: 'top left' });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight || 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchor.x;
    let top = anchor.y;
    let originX = 'left';
    let originY = 'top';
    if (left + MENU_W + 12 > vw) { left = Math.max(12, left - MENU_W); originX = 'right'; }
    if (top + h + 12 > vh) { top = Math.max(12, top - h); originY = 'bottom'; }
    setPos({ left, top, origin: `${originY} ${originX}` });
  }, [anchor.x, anchor.y]);

  useEffect(() => {
    const onDown = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    // capture: список чатов прокручивается во вложенном контейнере
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  const Item = ({ icon, label, onClick, danger }) => (
    <Box
      component={motion.div}
      whileHover={{ x: 2 }}
      onClick={() => { onClick(); onClose(); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.25, py: 0.875, borderRadius: '10px', cursor: 'pointer',
        color: danger ? 'danger.400' : 'text.primary',
        transition: 'background-color .12s',
        '&:hover': { backgroundColor: danger ? 'danger.softBg' : 'background.level1' },
        '& svg': {
          fontSize: 18,
          color: danger ? 'var(--joy-palette-danger-400)' : 'var(--joy-palette-text-tertiary)',
        },
      }}
    >
      {icon}
      <Typography level="body-sm" sx={{ fontWeight: 550, color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );

  const isPinned = Boolean(chat?.is_pinned);
  const canLeave = chat?.type_chat !== 'event' && chat?.type_chat !== 'direct' && !chat?.is_dm;
  const hasUnread = Number(chat?.unread_count || 0) > 0;

  return createPortal(
    <Box
      ref={ref}
      component={motion.div}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={MOTION.spring}
      onContextMenu={(e) => e.preventDefault()}
      sx={(theme) => ({
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        transformOrigin: pos.origin,
        width: MENU_W,
        zIndex: 12050,
        p: 0.625,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(16px)',
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(30,35,50,0.94)'
          : 'rgba(255,255,255,0.96)',
        boxShadow: '0 12px 32px rgba(16,24,40,0.18), 0 2px 8px rgba(16,24,40,0.08)',
      })}
    >
      <Typography
        level="body-xs"
        sx={{
          ...ellipsis,
          px: 1.25, pt: 0.25, pb: 0.75,
          color: 'text.tertiary', fontWeight: 700,
        }}
      >
        {chat?.display_name || chat?.name || `Чат №${chat?.chat_id}`}
      </Typography>

      <Item
        icon={isPinned ? <PushPinOutlinedIcon /> : <PushPinRoundedIcon />}
        label={isPinned ? 'Открепить чат' : 'Закрепить чат'}
        onClick={() => onTogglePin(chat, !isPinned)}
      />
      <Item
        icon={<CreateNewFolderRoundedIcon />}
        label="Добавить в папку"
        onClick={() => onAddToFolder(chat)}
      />
      {hasUnread && (
        <Item
          icon={<DoneAllRoundedIcon />}
          label="Отметить прочитанным"
          onClick={() => onMarkRead(chat)}
        />
      )}
      {canLeave && (
        <Item
          icon={<LogoutRoundedIcon />}
          label="Покинуть чат"
          danger
          onClick={() => onLeave(chat)}
        />
      )}
    </Box>,
    document.body,
  );
}
